import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatHeader } from './ChatHeader';
import { MessageBubble } from './MessageBubble';
import { ChatInput } from './ChatInput';
import { QuickActionChips } from './QuickActionChips';
import { TypingIndicator } from './TypingIndicator';
import { ExportMode } from './ExportMode';
import { ExportDialog } from './ExportDialog';
import { DataChart } from './DataChart';
import { DataHeatmap } from './DataHeatmap';
import { ChatService, type SupportedLanguage } from '../../services/chatService';
import { TrendingUp, BarChart3, Lightbulb, HelpCircle } from 'lucide-react';

// Language display names and native names
const LANGUAGE_DISPLAY: Record<SupportedLanguage, { name: string; native: string }> = {
  english: { name: 'English', native: 'English' },
  hindi: { name: 'Hindi', native: 'हिन्दी' },
  bengali: { name: 'Bengali', native: 'বাংলা' },
  telugu: { name: 'Telugu', native: 'తెలుగు' },
  marathi: { name: 'Marathi', native: 'मराठी' },
  tamil: { name: 'Tamil', native: 'தமிழ்' },
  gujarati: { name: 'Gujarati', native: 'ગુજરાતી' },
  kannada: { name: 'Kannada', native: 'ಕನ್ನಡ' },
  malayalam: { name: 'Malayalam', native: 'മലയാളം' },
  punjabi: { name: 'Punjabi', native: 'ਪੰਜਾਬੀ' },
  odia: { name: 'Odia', native: 'ଓଡ଼ିଆ' },
  arabic: { name: 'Arabic', native: 'العربية' },
  spanish: { name: 'Spanish', native: 'Español' },
  french: { name: 'French', native: 'Français' },
  german: { name: 'German', native: 'Deutsch' },
  chinese: { name: 'Chinese', native: '中文' },
  russian: { name: 'Russian', native: 'Русский' },
  japanese: { name: 'Japanese', native: '日本語' },
  portuguese: { name: 'Portuguese', native: 'Português' },
  italian: { name: 'Italian', native: 'Italiano' }
};

// All supported languages - Global languages first, then Indian languages
const ALL_LANGUAGES: SupportedLanguage[] = [
  // Global/International languages
  'english', 'arabic', 'spanish', 'french', 'german', 'chinese',
  'russian', 'japanese', 'portuguese', 'italian',
  // Indian languages
  'hindi', 'bengali', 'telugu', 'marathi', 'tamil',
  'gujarati', 'kannada', 'malayalam', 'punjabi', 'odia'
];

import type { Message, ChatbotProps, QuickAction, ReasoningEntry } from '../../types/chat';
import {
  defaultAPIConfig,
  defaultThemeConfig,
  defaultExportConfig,
  defaultVoiceConfig,
  defaultGovQueryConfig,
  defaultUILabels
} from '../../config/chatbot.config';
import './chatbot.css';
import { ReasoningPanel } from './ReasoningPanel';

export const Chatbot = ({
  // Basic Configuration
  placeholder = 'Type your message...',
  welcomeMessage = 'Hello! How can I assist you today?',
  language = 'english' as SupportedLanguage,

  // Feature Flags
  enableVoice = true,
  enableGovAPI = true,
  enablePersistence = false,

  // API Configuration
  apiConfig = defaultAPIConfig,

  // Voice Configuration
  voiceConfig = defaultVoiceConfig,

  // Export Configuration
  exportConfig = defaultExportConfig,

  // Theme Configuration
  theme = defaultThemeConfig,

  // UI Labels Configuration
  uiLabels = defaultUILabels,

  // Custom Quick Actions
  customQuickActions,

  // Callbacks
  callbacks = {},

  // Custom CSS Classes
  className,
  containerClassName,

  // Custom Components
  customHeader,
  customFooter,

  // Authentication
  getAuthToken,

  // Data Processing
  customMessageProcessor,
  customResponseParser
}: ChatbotProps) => {
  const navigate = useNavigate();
  // Extract callbacks with backwards compatibility
  const {
    onMessageSent,
    onResponseReceived,
    onSourceLinksReceived: onSourceLinksReceivedCallback,
    onFollowupQuestionsReceived: onFollowupQuestionsReceivedCallback,
    onInteractionIdReceived: onInteractionIdReceivedCallback,
    onError: onErrorCallback,
    onConnectionStateChange: onConnectionStateChangeCallback,
    onReasoningUpdate: onReasoningUpdateCallback
  } = callbacks;
  const [messages, setMessages] = useState<Message[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [streamingMode, setStreamingMode] = useState(false);
  const [isExportMode, setIsExportMode] = useState(false);
  const [selectedPairs, setSelectedPairs] = useState<Set<string>>(new Set());
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>(language);
  const [isQueryingGov, setIsQueryingGov] = useState(false);
  const [connectionState, setConnectionState] = useState<'disconnected' | 'connecting' | 'connected'>('disconnected');
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [lastSourceLinks, setLastSourceLinks] = useState<string[]>([]);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [followupQuestions, setFollowupQuestions] = useState<string[]>([]);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showContentDisplay, setShowContentDisplay] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<any>(null);
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const [contentTypes, setContentTypes] = useState<any[]>(
    exportConfig?.defaultContentTypes || defaultExportConfig.defaultContentTypes || []
  );
  const [isEditingGeneratedContent, setIsEditingGeneratedContent] = useState(false);
  const [editableGeneratedTitle, setEditableGeneratedTitle] = useState('');
  const [editableGeneratedBody, setEditableGeneratedBody] = useState('');
  useEffect(() => {
    if (showContentDisplay && generatedContent) {
      setEditableGeneratedTitle(generatedContent.title || '');
      setEditableGeneratedBody(generatedContent.content || '');
    } else {
      setIsEditingGeneratedContent(false);
      setEditableGeneratedTitle('');
      setEditableGeneratedBody('');
    }
  }, [showContentDisplay, generatedContent]);
  const [reasoningEntries, setReasoningEntries] = useState<ReasoningEntry[]>([]);
  const [reasoningStatus, setReasoningStatus] = useState<'idle' | 'connecting' | 'streaming' | 'error'>('idle');
  const [reasoningError, setReasoningError] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatServiceRef = useRef(new ChatService());
  const currentSourceLinksRef = useRef<string[]>([]);
  const currentFollowupQuestionsRef = useRef<string[]>([]);
  const currentInteractionIdRef = useRef<string | undefined>(undefined);
  const currentVisualizationRef = useRef<any>(undefined);
  const currentMessageIdRef = useRef<string | undefined>(undefined);

  const resetChatbotState = useCallback(
    (options: { resetLanguage?: boolean } = {}) => {
      const initialMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: welcomeMessage,
        timestamp: new Date()
      };

      // Core conversational state
      setMessages([initialMessage]);
      setIsProcessing(false);
      setStreamingMode(false);
      setIsExportMode(false);
      setSelectedPairs(new Set());

      // Contextual data
      setIsQueryingGov(false);
      setIsAudioPlaying(false);
      setLastSourceLinks([]);
      setVoiceError(null);
      setFollowupQuestions([]);
      setShowExportDialog(false);
      setShowContentDisplay(false);
      setGeneratedContent(null);
      setIsGeneratingContent(false);
      setReasoningEntries([]);
      setReasoningStatus('idle');
      setReasoningError(null);

      // Reset refs
      currentSourceLinksRef.current = [];
      currentFollowupQuestionsRef.current = [];
      currentInteractionIdRef.current = undefined;
      currentVisualizationRef.current = undefined;
      currentMessageIdRef.current = undefined;

      // Reset language if requested, otherwise ensure service uses current language
      if (options.resetLanguage) {
        setSelectedLanguage(language);
        chatServiceRef.current.setLanguage(language);
      } else {
        chatServiceRef.current.setLanguage(selectedLanguage);
      }

      // Ensure any active streams or sockets are closed
      chatServiceRef.current.disconnect();
    },
    [welcomeMessage, language, selectedLanguage]
  );

  // Dynamic quick actions based on follow-up questions from API
  const quickActions: QuickAction[] = React.useMemo(() => {
    // Use custom quick actions if provided
    if (customQuickActions && customQuickActions.length > 0) {
      return customQuickActions;
    }

    // If we have follow-up questions from the API, use those
    if (enableGovAPI && followupQuestions.length > 0) {
      return followupQuestions.map((question, index) => ({
        text: question,
        icon: [
          <TrendingUp key={index} className="w-3.5 h-3.5" />,
          <Lightbulb key={index} className="w-3.5 h-3.5" />,
          <HelpCircle key={index} className="w-3.5 h-3.5" />,
          <BarChart3 key={index} className="w-3.5 h-3.5" />
        ][index % 4]
      }));
    }

    // Otherwise show default government scheme categories
    if (enableGovAPI) {
      return [];
    }

    // Fallback to demo examples if gov API is disabled
    return [
      { text: 'Show bar chart example', icon: <BarChart3 className="w-3.5 h-3.5" /> },
      { text: 'Show line chart example', icon: <TrendingUp className="w-3.5 h-3.5" /> },
      { text: 'Show table example', icon: <Lightbulb className="w-3.5 h-3.5" /> },
      { text: 'Show heatmap example', icon: <HelpCircle className="w-3.5 h-3.5" /> },
    ];
  }, [customQuickActions, enableGovAPI, followupQuestions, selectedLanguage]);


  // Fetch content types from API
  const fetchContentTypes = async () => {
    // If no contentTypesEndpoint is provided, use default content types
    if (!apiConfig.contentTypesEndpoint) {
      console.log('No contentTypesEndpoint provided, using default content types');
      return;
    }

    try {


      const response = await fetch(
        `${apiConfig.baseUrl}${apiConfig.contentTypesEndpoint}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ data: {} })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch content types: ${response.statusText}`);
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        console.log('Fetched content types from API:', data);
        setContentTypes(data);
      } else {
        console.warn('Invalid content types response format:', data);
      }
    } catch (error) {
      console.error('Error fetching content types:', error);
      // Keep using default content types on error
    }
  };

  useEffect(() => {
    resetChatbotState();

    // Configure chat service with API settings (works with ANY app!)
    const chatService = chatServiceRef.current;
    chatService.setNavigator(navigate);
    chatService.configure({
      baseUrl: apiConfig.baseUrl,
      authEndpoint: apiConfig.authEndpoint || '/auth/getToken',
      queryEndpoint: apiConfig.queryEndpoint || '/agent/flow/execute',
      authPayload: apiConfig.authPayload,
      campaignId: apiConfig.campaignId || '',
      agentCode: apiConfig.agentCode || 'QUERY_DOCUMENTS_AND_RESPOND',
      customQueryParams: apiConfig.customQueryParams || {},
      reasoningWsUrl: apiConfig.reasoningWsUrl,
      orgCode: apiConfig.orgCode
    });
    // Set initial language
    chatService.setLanguage(selectedLanguage);

    chatService.setOnConnectionStateChange((state) => {
      setConnectionState(state);
      onConnectionStateChangeCallback?.(state);
    });
    chatService.setOnIsQueryingGov(setIsQueryingGov);
    chatService.setOnAudioPlayingChange(setIsAudioPlaying);
    chatService.setOnSourceLinksReceived((links) => {
      currentSourceLinksRef.current = links;
      setLastSourceLinks(links);
      onSourceLinksReceivedCallback?.(links);
    });
    chatService.setOnFollowupQuestionsReceived((questions) => {
      console.log('Callback: Received follow-up questions:', questions);
      currentFollowupQuestionsRef.current = questions;
      setFollowupQuestions(questions);
      onFollowupQuestionsReceivedCallback?.(questions);
    });
    chatService.setOnInteractionIdReceived((interactionId) => {
      console.log('Callback: Received interaction ID:', interactionId);
      currentInteractionIdRef.current = interactionId;
      onInteractionIdReceivedCallback?.(interactionId);
    });
    chatService.setOnVisualizationReceived((visualization) => {
      console.log('Callback: Received visualization data:', visualization);
      currentVisualizationRef.current = visualization;
    });
    chatService.setOnError((error) => {
      setVoiceError(error);
      onErrorCallback?.(error);
    });
    chatService.setOnReasoningUpdate((update) => {
      console.log('Callback: Received reasoning update:', update);
      onReasoningUpdateCallback?.(update);

      if (!update) return;

      switch (update.type) {
        case 'init':
          setReasoningEntries([]);
          setReasoningError(null);
          setReasoningStatus('connecting');
          break;
        case 'snapshot':
        case 'update':
          setReasoningEntries(update.reasonings || []);
          setReasoningError(null);
          setReasoningStatus(update.reasonings.length > 0 ? 'streaming' : 'connecting');
          break;
        case 'delta':
          {
            const appended = update.appendedReasonings ?? [];
            if (appended.length > 0) {
              setReasoningEntries((prev) => [...prev, ...appended]);
            }
          }
          setReasoningError(null);
          setReasoningStatus('streaming');
          break;
        case 'delete':
          setReasoningEntries([]);
          setReasoningError(null);
          setReasoningStatus('idle');
          break;
        case 'error':
          setReasoningError(update.error || 'Unable to stream reasoning.');
          setReasoningStatus('error');
          break;
      }
    });

    // Fetch content types
    fetchContentTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [welcomeMessage, selectedLanguage, navigate, resetChatbotState]);

  // Update campaignId when it changes
  useEffect(() => {
    if (apiConfig.campaignId) {
      console.log('Updating campaignId to:', apiConfig.campaignId);
      const currentLanguage = selectedLanguage; // Preserve current language
      chatServiceRef.current.configure({
        baseUrl: apiConfig.baseUrl,
        authEndpoint: apiConfig.authEndpoint || '/auth/getToken',
        queryEndpoint: apiConfig.queryEndpoint || '/agent/flow/execute',
        authPayload: apiConfig.authPayload,
        campaignId: apiConfig.campaignId,
        agentCode: apiConfig.agentCode || 'QUERY_DOCUMENTS_AND_RESPOND',
        customQueryParams: apiConfig.customQueryParams || {},
        reasoningWsUrl: apiConfig.reasoningWsUrl,
        orgCode: apiConfig.orgCode
      });
      // Restore language after configure
      chatServiceRef.current.setLanguage(currentLanguage);
      resetChatbotState({ resetLanguage: false }); // Don't reset language
      fetchContentTypes();
    }
  }, [
    apiConfig.campaignId,
    apiConfig.baseUrl,
    apiConfig.queryEndpoint,
    apiConfig.agentCode,
    apiConfig.authEndpoint,
    apiConfig.authPayload,
    apiConfig.customQueryParams,
    apiConfig.reasoningWsUrl,
    apiConfig.orgCode,
    selectedLanguage,
    resetChatbotState
  ]);

  // Initialize auth token from host app if available
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (getAuthToken) {
          const token = await getAuthToken();
          if (!cancelled && token) {
            chatServiceRef.current.setAuthToken(token);
          }
        }
      } catch (e) {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [getAuthToken]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (streamingMode) {
        chatServiceRef.current.disconnect();
      }
    };
  }, [streamingMode]);

  useEffect(() => {
    if (!isProcessing) {
      setReasoningEntries([]);
      setReasoningStatus('idle');
      setReasoningError(null);
    }
  }, [isProcessing]);

  useEffect(() => {
    const service = chatServiceRef.current;
    return () => {
      service.disconnect();
    };
  }, []);

  // Debug: Log when followupQuestions changes
  useEffect(() => {
    console.log('Follow-up questions updated:', followupQuestions);
    console.log('Quick actions count:', quickActions.length);
    if (quickActions.length > 0) {
      console.log('Quick action texts:', quickActions.map(q => q.text));
    }
  }, [followupQuestions, quickActions]);

  const handleSendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isProcessing) return;

    // Ensure language is set before making API call
    console.log('handleSendMessage: Current selectedLanguage:', selectedLanguage);
    chatServiceRef.current.setLanguage(selectedLanguage);
    console.log('handleSendMessage: Language set on service before API call');

    if (enableGovAPI) {
      setReasoningEntries([]);
      setReasoningStatus('connecting');
      setReasoningError(null);
    }

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date()
    };

    setMessages((prev) => [...prev, userMessage]);
    onMessageSent?.(content.trim());
    setIsProcessing(true);

    const assistantMessageId = crypto.randomUUID();
    const assistantMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true
    };

    setMessages((prev) => [...prev, assistantMessage]);

    // Reset source links, follow-up questions, interaction ID, and visualization for new message
    currentSourceLinksRef.current = [];
    setLastSourceLinks([]);
    currentInteractionIdRef.current = undefined;
    currentVisualizationRef.current = undefined;
    currentMessageIdRef.current = assistantMessageId;

    try {
      const response = await chatServiceRef.current.sendMessage(
        content.trim(),
        (streamedContent) => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantMessageId
                ? { ...msg, content: streamedContent }
                : msg
            )
          );
        }
      );

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantMessageId
            ? {
              ...msg,
              content: response,
              isStreaming: false,
              visualization: currentVisualizationRef.current,
              sourceLinks: currentSourceLinksRef.current.length > 0 ? currentSourceLinksRef.current : undefined,
              interactionId: currentInteractionIdRef.current
            }
            : msg
        )
      );

      onResponseReceived?.(response);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantMessageId
            ? { ...msg, content: 'Sorry, I encountered an error. Please try again.', isStreaming: false }
            : msg
        )
      );
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, onMessageSent, onResponseReceived, selectedLanguage, enableGovAPI]);

  const toggleStreamingMode = useCallback(() => {
    if (streamingMode) {
      chatServiceRef.current.disconnect();
      setStreamingMode(false);
      setVoiceError(null);
    } else {
      setStreamingMode(true);
      setVoiceError(null);
      // Connect to Gemini with voice
      chatServiceRef.current.connectToGemini((_message, sourceLinks) => {
        // This callback is for receiving messages in voice mode
        // We don't need to add messages here as they come via audio
        if (sourceLinks && sourceLinks.length > 0) {
          setLastSourceLinks(sourceLinks);
        }
      });
    }
  }, [streamingMode]);

  const handleExportMode = () => {
    setIsExportMode(true);
    setSelectedPairs(new Set());
  };

  const handleCancelExport = () => {
    setIsExportMode(false);
    setSelectedPairs(new Set());
  };

  const handlePairSelection = (pairId: string) => {
    setSelectedPairs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pairId)) {
        newSet.delete(pairId);
      } else {
        newSet.add(pairId);
      }
      return newSet;
    });
  };

  const handleExportAsIs = async () => {
    try {
      // Import PDF export function and axios
      const { generatePDFFromGeneratedContent, processMultiColumnData } = await import('../../utils/pdfExport');
      const axios = (await import('axios')).default;

      // Get interaction UIDs from selected pairs (from assistant messages)
      const interactionUIDs: string[] = [];
      Array.from(selectedPairs).forEach(pairId => {
        const index = parseInt(pairId.split('_')[1]);
        const assistantMessage = messages[index + 1];
        if (assistantMessage && assistantMessage.interactionId) {
          interactionUIDs.push(assistantMessage.interactionId);
        }
      });

      console.log('📤 [Export As-Is] Extracted interaction UIDs:', interactionUIDs);

      // Get auth token
      const token = localStorage.getItem('token');

      if (!token || interactionUIDs.length === 0) {
        console.warn('No token or interaction IDs available, falling back to local export');
        // Fallback to local content generation
        const selectedMessages: Message[] = [];
        Array.from(selectedPairs).forEach(pairId => {
          const index = parseInt(pairId.split('_')[1]);
          if (messages[index] && messages[index + 1]) {
            selectedMessages.push(messages[index], messages[index + 1]);
          }
        });

        const content = selectedMessages.map(msg => {
          return `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`;
        }).join('\n\n');

        const allSourceLinks: string[] = [];
        let visualization: Message['visualization'] | undefined;
        selectedMessages.forEach(msg => {
          if (msg.sourceLinks) {
            allSourceLinks.push(...msg.sourceLinks);
          }
          if (msg.visualization && !visualization) {
            visualization = msg.visualization;
          }
        });

        const generatedContent = {
          title: 'Conversation Export - As Is',
          content: content,
          format: 'Text Only',
          category: 'Conversation',
          sourceLinks: allSourceLinks.length > 0 ? Array.from(new Set(allSourceLinks)) : undefined,
          visualization: visualization
        };

        await generatePDFFromGeneratedContent(generatedContent);
        console.log('Export As-Is PDF generated successfully (fallback)');
        return;
      }

      // Call EXPORT_CONTENT_FROM_CONVERSATION API
      const payload = {
        data: {
          interactionUIDs: interactionUIDs
        },
        agent: {
          code: "EXPORT_CONTENT_FROM_CONVERSATION"
        }
      };

      console.log('📤 [Export As-Is] API Payload:', payload);

      const response = await axios.post(
        `${apiConfig.baseUrl}/agent/flow/execute`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      console.log('📤 [Export As-Is] API Response:', response.data);

      if (response.data.status === 200 && response.data.data && response.data.data.length > 0) {
        const responseData = response.data.data;
        const dictionaryResponses = responseData.filter((item: any) => item.objectType === 'DICTIONARY');

        // Extract title from DICTIONARY entries
        let title = 'Conversation Export - As Is'; // Default title
        for (const dict of dictionaryResponses) {
          if (dict.entries) {
            const titleEntry = dict.entries.find((entry: any) => entry.key === 'title');
            if (titleEntry?.value) {
              title = titleEntry.value;
              console.log('📝 [Export As-Is] Found title in API response:', title);
              break;
            }
          }
        }

        // Extract query and response from DICTIONARY entries
        let query = '';
        let responseText = '';
        let visualization: any = undefined;
        let sourceLinks: string[] = [];

        for (const dict of dictionaryResponses) {
          if (dict.entries) {
            const queryEntry = dict.entries.find((entry: any) => entry.key === 'query');
            const responseEntry = dict.entries.find((entry: any) => entry.key === 'response');
            const datasetEntry = dict.entries.find((entry: any) => entry.key === 'dataset');

            if (queryEntry?.value) query = queryEntry.value;
            if (responseEntry?.value) responseText = responseEntry.value;

            if (datasetEntry?.value) {
              const data = datasetEntry.value;
              const processedData = processMultiColumnData(data, data.visual);

              if (processedData) {
                let type: 'table' | 'chart' | 'heatmap' = 'table';

                if (processedData.visual === true) {
                  if (processedData.dimension === 2) {
                    type = 'chart';
                  } else if (processedData.dimension >= 3) {
                    type = 'heatmap';
                  } else {
                    type = 'chart';
                  }
                } else {
                  type = 'table';
                }

                visualization = { type, data: processedData };
              }
            }
          }
        }

        // Build final content
        const content = query && responseText
          ? `User: ${query}\n\nAssistant: ${responseText}`
          : responseText || 'Content exported successfully.';

        const generatedContent = {
          title: title,
          content: content,
          format: 'Text Only',
          category: 'Conversation',
          sourceLinks: sourceLinks.length > 0 ? sourceLinks : undefined,
          visualization: visualization
        };

        console.log('📤 [Export As-Is] Final content object:', generatedContent);

        // Generate PDF
        await generatePDFFromGeneratedContent(generatedContent);

        console.log('Export As-Is PDF generated successfully from API');
      } else {
        throw new Error('No export data received from API');
      }
    } catch (error) {
      console.error('Error exporting as-is:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      handleCancelExport();
    }
  };

  const handleGenerateContent = async () => {
    setIsExportMode(false);
    setShowExportDialog(true);
  };

  const handleExportDialogGenerate = async (brandDetails: any, contentPreferences: any) => {
    console.log('Generating content with:', { brandDetails, contentPreferences });

    try {
      setIsGeneratingContent(true);

      // Show loading state in preview dialog
      setGeneratedContent({
        title: uiLabels?.generatingContent || defaultUILabels.generatingContent || 'Generating...',
        content: 'Please wait while we generate your content...',
        format: contentPreferences.format,
        category: contentPreferences.category
      });
      setShowExportDialog(false);
      setShowContentDisplay(true);

      // Import all export functions
      const { generateContentFromAPI } = await import('../../utils/pdfExport');

      // Get interaction UIDs from selected pairs (from assistant messages)
      const interactionUIDs: string[] = [];
      Array.from(selectedPairs).forEach(pairId => {
        const index = parseInt(pairId.split('_')[1]);
        // Get the assistant message (index + 1) which has the interaction ID
        const assistantMessage = messages[index + 1];
        if (assistantMessage && assistantMessage.interactionId) {
          interactionUIDs.push(assistantMessage.interactionId);
        }
      });

      console.log('Extracted interaction IDs:', interactionUIDs);

      // Get the auth token from host app first, fallback to chat service auth
      let token: string | null = null;

      token = localStorage.getItem('token');
      let content;

      if (token && interactionUIDs.length > 0) {
        try {
          content = await generateContentFromAPI(
            interactionUIDs,
            brandDetails,
            contentPreferences,
            token
          );
        } catch (apiError) {
          console.error('API generation failed, using fallback:', apiError);
          // Fallback to local content generation
          content = createLocalGeneratedContent(brandDetails, contentPreferences);
        }
      } else {
        console.log('No token or interaction IDs available, using local generation');
        // Use local content generation
        content = createLocalGeneratedContent(brandDetails, contentPreferences);
      }

      // Show the generated content in preview dialog
      setGeneratedContent(content);

      console.log('Content generated successfully');
    } catch (error) {
      console.error('Error generating content:', error);
      // Show error or fallback content
      const fallbackContent = createLocalGeneratedContent({}, { format: 'Text Only', category: 'Content' });
      setGeneratedContent(fallbackContent);
      setShowExportDialog(false);
      setShowContentDisplay(true);
    } finally {
      setIsGeneratingContent(false);
    }
  };

  // Helper function to create content from local messages
  const createLocalGeneratedContent = (brandDetails: any, contentPreferences: any) => {
    const selectedMessages: Message[] = [];
    Array.from(selectedPairs).forEach(pairId => {
      const index = parseInt(pairId.split('_')[1]);
      if (messages[index] && messages[index + 1]) {
        selectedMessages.push(messages[index], messages[index + 1]);
      }
    });

    // Build content WITHOUT source links in the text
    const content = selectedMessages.map(msg => {
      return `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`;
    }).join('\n\n');

    // Collect source links and visualizations separately
    const allSourceLinks: string[] = [];
    let visualization: Message['visualization'] | undefined;
    selectedMessages.forEach(msg => {
      if (msg.sourceLinks) {
        allSourceLinks.push(...msg.sourceLinks);
      }
      if (msg.visualization && !visualization) {
        visualization = msg.visualization;
      }
    });

    return {
      title: `${contentPreferences.category} - Conversation Export`,
      content: content,
      format: contentPreferences.format,
      category: contentPreferences.category,
      sourceLinks: allSourceLinks.length > 0 ? Array.from(new Set(allSourceLinks)) : undefined,
      visualization: visualization
    };
  };

  const handleDownloadContent = async () => {
    if (!generatedContent) return;

    try {
      const { generatePDFFromGeneratedContent } = await import('../../utils/pdfExport');
      const contentToDownload = isEditingGeneratedContent
        ? {
          ...generatedContent,
          title: editableGeneratedTitle,
          content: editableGeneratedBody
        }
        : generatedContent;
      await generatePDFFromGeneratedContent(contentToDownload);
      console.log('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    }
  };

  const handleCopyContent = () => {
    if (!generatedContent) return;

    const contentToCopy = isEditingGeneratedContent
      ? {
        ...generatedContent,
        title: editableGeneratedTitle,
        content: editableGeneratedBody
      }
      : generatedContent;

    // Build comprehensive content for copying
    let fullContent = '';
    fullContent += `${contentToCopy.title}\n\n`;
    fullContent += `Format: ${contentToCopy.format}\n`;
    fullContent += `Category: ${contentToCopy.category}\n\n`;
    fullContent += `---\n\n`;
    fullContent += `Content:\n\n`;
    fullContent += contentToCopy.content + '\n\n';

    if (contentToCopy.sourceLinks && contentToCopy.sourceLinks.length > 0) {
      fullContent += `Source Links:\n\n`;
      contentToCopy.sourceLinks.forEach((link: string, index: number) => {
        fullContent += `${index + 1}. ${link}\n`;
      });
      fullContent += '\n';
    }

    fullContent += `---\n\n`;
    fullContent += `Generated on: ${new Date().toLocaleString()}\n`;

    navigator.clipboard.writeText(fullContent);
    alert('Content copied to clipboard!');
  };

  const handleSaveEditedGeneratedContent = () => {
    if (!generatedContent) return;
    setGeneratedContent((prev: any) =>
      prev
        ? {
          ...prev,
          title: editableGeneratedTitle,
          content: editableGeneratedBody
        }
        : prev
    );
    setIsEditingGeneratedContent(false);
  };

  const handleCancelEditingGeneratedContent = () => {
    if (generatedContent) {
      setEditableGeneratedTitle(generatedContent.title || '');
      setEditableGeneratedBody(generatedContent.content || '');
    }
    setIsEditingGeneratedContent(false);
  };

  const handleCloseContentDisplay = () => {
    setShowContentDisplay(false);
    setGeneratedContent(null);
    setSelectedPairs(new Set());
    setIsEditingGeneratedContent(false);
  };

  const handleQuickAction = (text: string) => {
    // If it's a follow-up question or government scheme query, send it directly
    if (enableGovAPI && (
      followupQuestions.includes(text) || // Dynamic follow-up questions
      text.includes('योजना') || text.includes('Schemes') ||
      text.includes('Farmer') || text.includes('Women') ||
      text.includes('Education') || text.includes('Health')
    )) {
      handleSendMessage(text);
      return;
    }

    if (text === 'Show bar chart example') {
      const userMessage: Message = {
        id: crypto.randomUUID(),
        role: 'user',
        content: text,
        timestamp: new Date()
      };

      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: 'Here is a bar chart showing sales by product category:',
        timestamp: new Date(),
        visualization: {
          type: 'chart',
          data: {
            title: 'Sales by Product Category',
            headers: ['category', 'sales'],
            dimension: 2,
            data: [
              { category: 'Electronics', sales: 45000 },
              { category: 'Clothing', sales: 32000 },
              { category: 'Home & Garden', sales: 28000 },
              { category: 'Sports', sales: 21000 },
              { category: 'Books', sales: 15000 },
              { category: 'Toys', sales: 12000 },
            ]
          }
        }
      };

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
    } else if (text === 'Show line chart example') {
      const userMessage: Message = {
        id: crypto.randomUUID(),
        role: 'user',
        content: text,
        timestamp: new Date()
      };

      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: 'Here is a line chart showing revenue trends by region over time:',
        timestamp: new Date(),
        visualization: {
          type: 'chart',
          data: {
            title: 'Revenue Trends by Region',
            headers: ['region', 'quarter', 'revenue'],
            dimension: 3,
            data: [
              { region: 'North', quarter: 'Q1', revenue: 25000 },
              { region: 'North', quarter: 'Q2', revenue: 28000 },
              { region: 'North', quarter: 'Q3', revenue: 32000 },
              { region: 'North', quarter: 'Q4', revenue: 35000 },
              { region: 'South', quarter: 'Q1', revenue: 18000 },
              { region: 'South', quarter: 'Q2', revenue: 22000 },
              { region: 'South', quarter: 'Q3', revenue: 26000 },
              { region: 'South', quarter: 'Q4', revenue: 30000 },
              { region: 'East', quarter: 'Q1', revenue: 22000 },
              { region: 'East', quarter: 'Q2', revenue: 24000 },
              { region: 'East', quarter: 'Q3', revenue: 27000 },
              { region: 'East', quarter: 'Q4', revenue: 29000 },
              { region: 'West', quarter: 'Q1', revenue: 20000 },
              { region: 'West', quarter: 'Q2', revenue: 23000 },
              { region: 'West', quarter: 'Q3', revenue: 28000 },
              { region: 'West', quarter: 'Q4', revenue: 33000 },
            ]
          }
        }
      };

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
    } else if (text === 'Show table example') {
      const userMessage: Message = {
        id: crypto.randomUUID(),
        role: 'user',
        content: text,
        timestamp: new Date()
      };

      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: 'Here is a detailed table showing employee information:',
        timestamp: new Date(),
        visualization: {
          type: 'table',
          data: {
            title: 'Employee Directory',
            headers: ['Name', 'Department', 'Position', 'Years', 'Skills'],
            data: [
              {
                Name: 'John Smith',
                Department: 'Engineering',
                Position: 'Senior Developer',
                Years: 5,
                Skills: ['JavaScript', 'React', 'Node.js', 'Python']
              },
              {
                Name: 'Sarah Johnson',
                Department: 'Marketing',
                Position: 'Marketing Manager',
                Years: 3,
                Skills: ['SEO', 'Content Strategy', 'Analytics', 'Social Media']
              },
              {
                Name: 'Michael Chen',
                Department: 'Engineering',
                Position: 'DevOps Engineer',
                Years: 4,
                Skills: ['AWS', 'Docker', 'Kubernetes', 'CI/CD']
              },
              {
                Name: 'Emily Davis',
                Department: 'Design',
                Position: 'UI/UX Designer',
                Years: 2,
                Skills: ['Figma', 'Adobe XD', 'Prototyping', 'User Research']
              },
              {
                Name: 'David Wilson',
                Department: 'Sales',
                Position: 'Sales Director',
                Years: 7,
                Skills: ['B2B Sales', 'Negotiation', 'CRM', 'Team Leadership']
              },
            ]
          }
        }
      };

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
    } else if (text === 'Show heatmap example') {
      const userMessage: Message = {
        id: crypto.randomUUID(),
        role: 'user',
        content: text,
        timestamp: new Date()
      };

      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: 'Here is a heatmap showing customer satisfaction scores by region and time period:',
        timestamp: new Date(),
        visualization: {
          type: 'heatmap',
          data: {
            title: 'Customer Satisfaction Scores',
            headers: ['Region', 'Month', 'Score'],
            dimension: 3,
            data: [
              { Region: 'North', Month: 'Jan', Score: 85 },
              { Region: 'North', Month: 'Feb', Score: 88 },
              { Region: 'North', Month: 'Mar', Score: 90 },
              { Region: 'North', Month: 'Apr', Score: 87 },
              { Region: 'North', Month: 'May', Score: 92 },
              { Region: 'North', Month: 'Jun', Score: 94 },
              { Region: 'South', Month: 'Jan', Score: 78 },
              { Region: 'South', Month: 'Feb', Score: 82 },
              { Region: 'South', Month: 'Mar', Score: 85 },
              { Region: 'South', Month: 'Apr', Score: 88 },
              { Region: 'South', Month: 'May', Score: 90 },
              { Region: 'South', Month: 'Jun', Score: 91 },
              { Region: 'East', Month: 'Jan', Score: 82 },
              { Region: 'East', Month: 'Feb', Score: 84 },
              { Region: 'East', Month: 'Mar', Score: 86 },
              { Region: 'East', Month: 'Apr', Score: 89 },
              { Region: 'East', Month: 'May', Score: 91 },
              { Region: 'East', Month: 'Jun', Score: 93 },
              { Region: 'West', Month: 'Jan', Score: 80 },
              { Region: 'West', Month: 'Feb', Score: 83 },
              { Region: 'West', Month: 'Mar', Score: 87 },
              { Region: 'West', Month: 'Apr', Score: 90 },
              { Region: 'West', Month: 'May', Score: 92 },
              { Region: 'West', Month: 'Jun', Score: 95 },
            ]
          }
        }
      };

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
    } else {
      handleSendMessage(text);
    }
  };

  return (
    <div className={containerClassName || `chatbot-container relative flex flex-col bg-white border border-gray-200 rounded-3xl shadow-2xl overflow-hidden transition-all duration-300 ${isExpanded ? 'w-full h-screen max-w-full rounded-none' : 'w-full h-full'
      } ${className || ''}`} style={{ height: '100%' }}>
      {customHeader || (
        <ChatHeader
          title={uiLabels?.title || defaultUILabels.title || 'Chatbot'}
          subtitle={
            streamingMode
              ? (connectionState === 'connecting'
                ? uiLabels?.connecting || defaultUILabels.connecting
                : connectionState === 'connected'
                  ? (isQueryingGov
                    ? uiLabels?.queryingDatabase || defaultUILabels.queryingDatabase
                    : isAudioPlaying
                      ? uiLabels?.speaking || defaultUILabels.speaking
                      : uiLabels?.listening || defaultUILabels.listening)
                  : 'Voice Active')
              : isQueryingGov
                ? 'Querying  database...'
                : 'Online'
          }
          isExpanded={isExpanded}
          onToggleExpand={() => setIsExpanded(!isExpanded)}
          streamingMode={streamingMode}
          onToggleStreaming={enableVoice ? toggleStreamingMode : undefined}
          onToggleExport={!isExportMode && !streamingMode && exportConfig?.enableExport ? handleExportMode : undefined}
          isExportMode={isExportMode}
          selectedLanguage={selectedLanguage}
          onLanguageChange={(lang) => {
            console.log('Chatbot: Language changed to:', lang);
            setSelectedLanguage(lang);
            chatServiceRef.current.setLanguage(lang);
            console.log('Chatbot: Language state updated and service notified');
          }}
          enableGovAPI={enableGovAPI}
          allLanguages={ALL_LANGUAGES}
          languageDisplay={LANGUAGE_DISPLAY}
        />
      )}


      {/* Voice Error Display */}
      {voiceError && streamingMode && (
        <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
          {voiceError}
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-6 py-8 bg-white custom-scrollbar min-h-0">
        {!streamingMode && !isExportMode && messages.map((message) => {
          // Don't show empty or streaming messages with no content
          if (message.isStreaming && !message.content.trim()) {
            return null;
          }
          return (
            <MessageBubble
              key={message.id}
              message={message}
            />
          );
        })}

        {!streamingMode && isExportMode && messages.map((message, index) => {
          const isUserMessage = message.role === 'user';
          const nextMessage = messages[index + 1];
          const isPairComplete = isUserMessage && nextMessage && nextMessage.role === 'assistant';
          const pairId = isPairComplete ? `pair_${index}` : null;

          // Render complete user-bot pairs
          if (isPairComplete) {
            return (
              <div
                key={pairId}
                className={`relative mb-6 ml-12 border-2 rounded-xl p-4 cursor-pointer transition-all duration-200 ${selectedPairs.has(pairId!)
                    ? 'border-[#4c3794] bg-purple-50 shadow-md'
                    : 'border-gray-200 bg-white hover:border-purple-200 hover:shadow-sm'
                  }`}
                onClick={() => handlePairSelection(pairId!)}
              >
                {/* Checkbox for selection */}
                <div className="absolute -left-9 top-4 bg-white border border-gray-200 rounded shadow-sm p-1.5">
                  <input
                    type="checkbox"
                    checked={selectedPairs.has(pairId!)}
                    onChange={() => handlePairSelection(pairId!)}
                    className="w-4 h-4 accent-[#4c3794] cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                {/* User message */}
                <div className="mb-4">
                  <MessageBubble message={message} />
                </div>

                {/* Assistant message */}
                <MessageBubble message={nextMessage} />
              </div>
            );
          }

          // Skip rendering bot messages that are part of a pair
          if (!isUserMessage && index > 0) {
            const prevMessage = messages[index - 1];
            if (prevMessage.role === 'user') {
              return null;
            }
          }

          // Render standalone messages (not part of complete pairs)
          return <MessageBubble key={message.id} message={message} />;
        })}
        {!streamingMode && isProcessing && <TypingIndicator />}

        {!streamingMode && isProcessing && (
          <ReasoningPanel
            entries={reasoningEntries}
            status={reasoningStatus}
            error={reasoningError}
            interactionId={currentInteractionIdRef.current}
          />
        )}



        {streamingMode && (
          <div className="flex flex-col items-center justify-center h-full space-y-8">

            <div className="flex flex-col items-center space-y-6 relative">
              {/* Animated Orbs */}
              {connectionState === 'connected' && (
                <div className="relative w-64 h-64 flex items-center justify-center">
                  <div className="absolute inset-0 flex items-center justify-center">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute rounded-full bg-[#4c3794] opacity-20"
                        style={{
                          width: `${80 + i * 40}px`,
                          height: `${80 + i * 40}px`,
                          animation: `pulse-orb ${2 + i * 0.5}s ease-in-out infinite`,
                          animationDelay: `${i * 0.3}s`,
                        }}
                      />
                    ))}
                    {isAudioPlaying && [...Array(5)].map((_, i) => (
                      <div
                        key={`wave-${i}`}
                        className="absolute rounded-full bg-gradient-to-r from-blue-400 to-cyan-400"
                        style={{
                          width: '12px',
                          height: '12px',
                          animation: `float-orb ${3 + Math.random() * 2}s ease-in-out infinite`,
                          animationDelay: `${Math.random() * 2}s`,
                          left: `${20 + Math.random() * 60}%`,
                          top: `${20 + Math.random() * 60}%`,
                        }}
                      />
                    ))}
                    {isQueryingGov && [...Array(3)].map((_, i) => (
                      <div
                        key={`gov-${i}`}
                        className="absolute rounded-full bg-gradient-to-r from-green-400 to-emerald-400"
                        style={{
                          width: '8px',
                          height: '8px',
                          animation: `pulse-orb ${1.5 + i * 0.3}s ease-in-out infinite`,
                          animationDelay: `${i * 0.2}s`,
                          left: `${30 + Math.random() * 40}%`,
                          top: `${30 + Math.random() * 40}%`,
                        }}
                      />
                    ))}
                  </div>
                  <div className="relative z-10 w-20 h-20 rounded-full bg-gradient-to-br from-[#4c3794] to-[#3a2a73] flex items-center justify-center shadow-2xl">
                    <div className={`w-3 h-3 rounded-full bg-white ${connectionState === 'connected' ? 'animate-pulse' : ''
                      }`} />
                  </div>
                </div>
              )}

              {connectionState !== 'connected' && (
                <div className="w-20 h-20 rounded-full bg-[#4c3794] flex items-center justify-center shadow-lg">
                  <div className="w-3 h-3 rounded-full bg-white" />
                </div>
              )}

              <div className="text-center space-y-3">
                <p className="text-gray-900 text-lg font-medium">
                  {connectionState === 'connecting'
                    ? 'Connecting...'
                    : connectionState === 'connected'
                      ? (isQueryingGov
                        ? 'Querying database...'
                        : isAudioPlaying
                          ? 'Speaking...'
                          : 'Listening...')
                      : 'Click the mic button to start'}
                </p>
              </div>

              {/* Display source links in voice mode */}
              {lastSourceLinks.length > 0 && connectionState === 'connected' && (
                <div className="max-w-md p-4 bg-green-50 border border-green-200 rounded-xl">
                  <h3 className="text-sm font-semibold text-green-900 mb-2">
                    📄 Reference Documents
                  </h3>
                  <div className="space-y-2">
                    {lastSourceLinks.map((link, index) => (
                      <a
                        key={index}
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-xs text-blue-600 hover:text-blue-800 underline break-all"
                      >
                        Document {index + 1}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {!streamingMode && !isExportMode && quickActions.length > 0 && (
        <QuickActionChips actions={quickActions} onActionClick={handleQuickAction} />
      )}

      {!streamingMode && isExportMode && (
        <ExportMode
          selectedCount={selectedPairs.size}
          onCancel={handleCancelExport}
          onExportAsIs={handleExportAsIs}
          onGenerateContent={handleGenerateContent}
        />
      )}

      {!streamingMode && !isExportMode && (
        <ChatInput
          onSend={handleSendMessage}
          disabled={isProcessing}
          placeholder={placeholder}
          streamingMode={false}
          transcript=""
        />
      )}

      {/* Export Dialog */}
      <ExportDialog
        isOpen={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        onGenerate={handleExportDialogGenerate}
        contentTypes={contentTypes}
      />

      {/* Generated Content Display Dialog */}
      {showContentDisplay && generatedContent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col border-2 border-[#4c3794]">
            {/* Header */}
            <div className="bg-[#4c3794] p-6 border-b border-[#3a2a73]">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                    <span className="text-[#4c3794] text-lg font-bold">✓</span>
                  </div>
                  <div>
                    <h2 className="text-white text-xl font-semibold">Generated Content</h2>
                    <p className="text-purple-200 text-sm">
                      {generatedContent.category} • {generatedContent.format}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isEditingGeneratedContent ? (
                    <>
                      <button
                        onClick={handleCancelEditingGeneratedContent}
                        className="px-3 py-2 text-sm text-white/80 hover:text-white transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveEditedGeneratedContent}
                        className="px-3 py-2 text-sm bg-white text-[#4c3794] rounded-lg font-medium hover:bg-purple-100 transition-colors"
                      >
                        Save Changes
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setIsEditingGeneratedContent(true)}
                      className="px-3 py-2 text-sm text-white/90 hover:text-white underline underline-offset-4"
                    >
                      Edit Content
                    </button>
                  )}
                  <button
                    onClick={handleCloseContentDisplay}
                    className="text-white hover:bg-[#3a2a73] rounded-lg p-2 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {isGeneratingContent ? (
                <div className="flex flex-col items-center justify-center min-h-[200px] gap-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4c3794]"></div>
                  <h3 className="text-gray-900 text-lg font-semibold">{uiLabels?.generatingContent || defaultUILabels.generatingContent}</h3>
                  <p className="text-gray-600 text-center max-w-md">
                    Please wait while we create your content based on the selected conversation pairs.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Title */}
                  <div>
                    {isEditingGeneratedContent ? (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Title
                        </label>
                        <input
                          type="text"
                          value={editableGeneratedTitle}
                          onChange={(e) => setEditableGeneratedTitle(e.target.value)}
                          className="w-full rounded-xl border border-gray-300 px-4 py-2 text-lg font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#4c3794] focus:border-transparent"
                          maxLength={160}
                        />
                      </div>
                    ) : (
                      <h3 className="text-2xl font-semibold text-[#4c3794] mb-2">
                        {generatedContent.title}
                      </h3>
                    )}
                  </div>

                  {/* Main Content */}
                  {isEditingGeneratedContent ? (
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Content
                      </label>
                      <textarea
                        value={editableGeneratedBody}
                        onChange={(e) => setEditableGeneratedBody(e.target.value)}
                        className="w-full min-h-[240px] rounded-xl border border-gray-300 px-4 py-3 text-sm text-gray-900 leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#4c3794] focus:border-transparent custom-scrollbar resize-y"
                      />
                      <p className="text-xs text-gray-500">
                        Tip: make final tweaks here before downloading or copying.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <div className="prose prose-sm max-w-none text-gray-900 whitespace-pre-wrap">
                        {generatedContent.content}
                      </div>
                    </div>
                  )}

                  {/* Visualization */}
                  {generatedContent.visualization && (
                    <div className="bg-white rounded-xl p-6 border border-gray-200">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Data Visualization</h4>
                      {generatedContent.visualization.type === 'chart' && (
                        <DataChart data={generatedContent.visualization.data} />
                      )}
                      {generatedContent.visualization.type === 'heatmap' && (
                        <DataHeatmap data={generatedContent.visualization.data} />
                      )}
                      {generatedContent.visualization.type === 'table' && (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                {generatedContent.visualization.data.headers?.map((header: string, i: number) => (
                                  <th key={i} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {header}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {generatedContent.visualization.data.data?.slice(0, 10).map((row: any, i: number) => (
                                <tr key={i}>
                                  {generatedContent.visualization.data.headers?.map((header: string, j: number) => (
                                    <td key={j} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                      {row[header]}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Source Links */}
                  {generatedContent.sourceLinks && generatedContent.sourceLinks.length > 0 && (
                    <div className="bg-white rounded-xl p-6 border border-gray-200">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Source Links</h4>
                      <div className="space-y-2">
                        {generatedContent.sourceLinks.map((link: string, index: number) => (
                          <a
                            key={index}
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-sm text-[#4c3794] hover:text-[#3a2a73] underline break-all"
                          >
                            {link}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="border-t border-gray-200 p-6 bg-gray-50 flex justify-between items-center">
              <button
                onClick={handleCloseContentDisplay}
                className="px-6 py-2 text-gray-600 hover:text-gray-900 font-medium transition-colors"
              >
                Close
              </button>
              <div className="flex gap-3">
                {(generatedContent.format === 'Text Only' || generatedContent.format === 'Video') && (
                  <button
                    onClick={handleCopyContent}
                    className="px-6 py-2 bg-white text-[#4c3794] border-2 border-[#4c3794] rounded-lg font-medium hover:bg-purple-50 transition-colors"
                  >
                    Copy Content
                  </button>
                )}
                <button
                  onClick={handleDownloadContent}
                  className="px-6 py-2 bg-[#4c3794] text-white rounded-lg font-medium hover:bg-[#3a2a73] transition-colors flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a 3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Footer */}
      {customFooter}
    </div>
  );
};
